import { useQueryClient } from '@tanstack/react-query';
import Router from 'next/router';
import { useCallback } from 'react';

import { Account, Auth } from '@air/api';
import { matchesAirror, WORKSPACE_INTEGRATION_SSO_NOT_ENABLED } from '@air/errors';
import { getCurrentSessionKey, getCurrentUserInfoKey } from '@air/utils-auth';

import { Routes } from '~/constants/routes';
import { QueryParamNames } from '~/constants/search';
import { getAccountKey } from '~/swr-hooks/account/useAccount';
import { getListApprovedWorkspacesKey } from '~/swr-hooks/workspaces/useListApprovedWorkspaces';
import { EMAIL_VERIFICATION_CUTOFF_DATE, shouldGateUserForEmailVerification } from '~/utils/emailVerificationGate';
import { getSkipOnboardingEnabledFromQuery } from '~/utils/getSkipOnboardingEnabledFromQuery';
import { EMAIL_VERIFICATION_REQUIRED_REDIRECT_KEY } from '~/utils/SessionHelpers';

const getQueryParamFromRedirect = (redirect: string | undefined, paramName: QueryParamNames): string | undefined => {
  if (!redirect) {
    return undefined;
  }

  try {
    const url = new URL(redirect, window.location.origin);

    return url.searchParams.get(paramName) ?? undefined;
  } catch {
    return undefined;
  }
};

export const useHandleNoWorkspaces = () => {
  const queryClient = useQueryClient();

  const handleNoWorkspaces = useCallback(
    async (redirect?: string) => {
      Router.ready(async () => {
        const [userInfo, approvedWorkspaces, account] = await Promise.all([
          queryClient.fetchQuery({
            queryKey: getCurrentUserInfoKey(),
            queryFn: Auth.getCurrentUserInfo,
          }),
          queryClient.fetchQuery({
            queryKey: getListApprovedWorkspacesKey(),
            queryFn: () =>
              Account.listApprovedWorkspaces().catch((error) => {
                if (matchesAirror(error, WORKSPACE_INTEGRATION_SSO_NOT_ENABLED)) {
                  return { data: [] };
                } else {
                  throw error;
                }
              }),
          }),
          queryClient.fetchQuery({
            queryKey: getAccountKey(),
            queryFn: Account.get,
          }),
        ]);

        queryClient.invalidateQueries({ queryKey: getCurrentSessionKey() });
        const skipOnboardingFromRedirect = getQueryParamFromRedirect(redirect, QueryParamNames.skipOnboarding);
        const shouldSkipOnboarding =
          skipOnboardingFromRedirect === 'true' ||
          (skipOnboardingFromRedirect === undefined && getSkipOnboardingEnabledFromQuery(Router.query));

        if (shouldSkipOnboarding) {
          // From `/sign-up` (redirect provided): push so HomeLayout mounts and
          // useHandleSkipOnboarding can create the workspace; the redirect URL already
          // carries skipOnboarding=true plus any deep-link params (canvasName, etc.).
          // From inside HomeLayout (useCheckIfUserHasWorkspaces, no redirect): we're
          // already on the right surface — pushing to Routes.home would strip the live
          // query and race useHandleSkipOnboarding before it can read the deep-link.
          if (redirect) {
            Router.push(redirect);
          }

          return;
        }

        const shouldGateUser = shouldGateUserForEmailVerification({
          accountCreatedAt: account.createdAt,
          cutoffIsoDate: EMAIL_VERIFICATION_CUTOFF_DATE,
          isEmailVerified: userInfo.attributes.email_verified,
          workspaceCount: 0,
        });

        if (shouldGateUser) {
          const redirectTarget = redirect ?? `${window.location.pathname}${window.location.search}`;
          window.sessionStorage.setItem(EMAIL_VERIFICATION_REQUIRED_REDIRECT_KEY, redirectTarget);
          Router.push(Routes.auth.verifyEmailRequired);
          return;
        }

        /**
         * If they have an unverified email or have never created a workspace,
         * send them to create a workspace. We don't allow unverified users to see
         * the join available workspace screen because malicious users could join workspaces
         * with emails that aren't really theirs.
         */
        if (!userInfo.attributes.email_verified || !approvedWorkspaces.data.length) {
          Router.push(Routes.onboarding.createWorkspace);
        } else {
          Router.push({
            pathname: Routes.joinApprovedWorkspace,
            query: {
              redirect,
            },
          });
        }
      });
    },
    [queryClient],
  );

  return {
    handleNoWorkspaces,
  };
};
